import React, { useState, useEffect, useCallback } from "react";
import { Layout, message, Descriptions, Table, Button, Space, Popconfirm, Input, Row, Col, Tag, Breadcrumb } from "antd";
import { EyeOutlined, CheckOutlined, CloseOutlined, ShoppingCartOutlined, HomeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { useParams, useNavigate } from "react-router-dom";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { TextArea } = Input;

const PRApprovalDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [prDetails, setPrDetails] = useState(null);
    const [prItems, setPrItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [comment, setComment] = useState("");

    const fetchPRDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/inventory/pr/get.php?id=${id}`);
            if (response.data.status === "success") {
                setPrDetails(response.data.master);
                setPrItems(response.data.items || []);
            } else {
                message.error(response.data.message || "Failed to load Purchase Request details.");
            }
        } catch (error) {
            message.error("Failed to fetch Purchase Request details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchPRDetails();
    }, [fetchPRDetails]);

    const handleApproval = async (status) => {
        setProcessing(true);
        try {
            const response = await api.post("/inventory/pr/approve.php", {
                pr_id: parseInt(id),
                status: status,
                approved_comment: comment
            });

            if (response.data.status === "success") {
                message.success(response.data.message);
                navigate("/inventory/pr/approvals");
            } else {
                message.error(response.data.message || "Failed to process review.");
            }
        } catch (error) {
            message.error("Failed to process approval.");
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <Layout className="fluid-page">
                <Headerdiv />
                <Content className="fluid-content">
                    <div className="fluid-card" style={{ textAlign: 'center', padding: 50 }}>Loading...</div>
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    if (!prDetails) {
        return (
            <Layout className="fluid-page">
                <Headerdiv />
                <Content className="fluid-content">
                    <div className="fluid-card" style={{ padding: 24, textAlign: 'center' }}>Purchase Request not found</div>
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    const renderStatusTag = (status) => {
        return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { href: '/inventory-items', title: 'Inventory' },
                                { href: '/inventory/pr/approvals', title: 'PR Approvals' },
                                { title: `Review: ${prDetails.pr_ref}` },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <EyeOutlined /> Review Request: {prDetails.pr_ref}
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Review and take action on this purchase request</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card" style={{ marginBottom: 16 }}>
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">
                            <ShoppingCartOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
                            Purchase Request Summary
                        </h3>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/inventory/pr/approvals")}>
                            Back to Approvals
                        </Button>
                    </div>
                    <Descriptions bordered size="small" column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}>
                        <Descriptions.Item label="PR Reference"><strong>{prDetails.pr_ref}</strong></Descriptions.Item>
                        <Descriptions.Item label="Supplier">{prDetails.supplier_name}</Descriptions.Item>
                        <Descriptions.Item label="Date">{dayjs(prDetails.pr_date).format('YYYY-MM-DD')}</Descriptions.Item>
                        <Descriptions.Item label="Expected Delivery">{prDetails.delivery_expected_date ? dayjs(prDetails.delivery_expected_date).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                        <Descriptions.Item label="Status">{renderStatusTag(prDetails.pr_status)}</Descriptions.Item>
                        <Descriptions.Item label="Created By">{prDetails.creator_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Notes" span={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}>{prDetails.ipo_note || '-'}</Descriptions.Item>
                    </Descriptions>
                </div>

                <div className="fluid-card" style={{ marginTop: 16 }}>
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Items Review Table</h3>
                    </div>
                    <div className="fluid-table">
                        <Table
                            dataSource={prItems}
                            rowKey="pritem_id"
                            pagination={false}
                            bordered
                            size="small"
                            scroll={{ x: 'max-content' }}
                            columns={[
                                { title: "Item Code", dataIndex: "inv_code", key: "inv_code", render: (val) => val || "-" },
                                {
                                    title: "Item Description",
                                    dataIndex: "inv_desc",
                                    key: "inv_desc",
                                    render: (val, record) => (
                                        <div>
                                            <strong>{val || "-"}</strong>
                                            {record.item_note && (
                                                <div style={{ fontSize: "12px", color: "var(--fluid-harvest)", marginTop: 2, fontWeight: "500" }}>
                                                    Note: {record.item_note}
                                                </div>
                                            )}
                                        </div>
                                    )
                                },
                                { title: "Order Qty", dataIndex: "order_qty", key: "order_qty", align: "right", render: (qty) => <strong>{Number(qty).toFixed(2)}</strong> },
                                { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", align: "right", render: (val) => `LKR ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                                { title: "Tax", dataIndex: "tax_price", key: "tax_price", align: "right", render: (val) => `LKR ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                                { title: "Discount", dataIndex: "discount_price", key: "discount_price", align: "right", render: (val) => `LKR ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                                {
                                    title: "Sub Total",
                                    dataIndex: "item_subtotal",
                                    key: "item_subtotal",
                                    align: "right",
                                    render: (val) => <strong>LKR {Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                                }
                            ]}
                        />
                    </div>

                    <div style={{ marginTop: 24, textAlign: "right" }}>
                        <Descriptions column={1} size="small" bordered style={{ width: 340, display: "inline-block" }}>
                            <Descriptions.Item label="Sub Total">LKR {parseFloat(prDetails.pr_sub_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                            <Descriptions.Item label="Total Tax">LKR {parseFloat(prDetails.pr_total_tax || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                            <Descriptions.Item label="Total Discount">
                                <span style={{ color: '#cf1322' }}>- LKR {parseFloat(prDetails.pr_total_discount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Grand Total">
                                <strong style={{ fontSize: "16px", color: "var(--fluid-primary)" }}>
                                    LKR {parseFloat(prDetails.pr_final_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </strong>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                </div>

                {prDetails.pr_status === 'Pending' || prDetails.pr_status === 'Pending Approval' ? (
                    <div className="fluid-card" style={{ marginTop: 16 }}>
                        <div className="fluid-card-header">
                            <h3 className="fluid-card-title">Approval Decision</h3>
                        </div>
                        <Row gutter={24}>
                            <Col xs={24} md={16}>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: "var(--fluid-text)" }}>Review Remarks (Optional)</label>
                                    <TextArea
                                        rows={4}
                                        placeholder="Add notes explaining your decision..."
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} md={8} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Popconfirm title="Approve this request?" onConfirm={() => handleApproval('Approved')} okText="Yes" cancelText="No">
                                        <Button type="primary" block icon={<CheckOutlined />} className="fluid-btn-primary" loading={processing}>
                                            Approve Request
                                        </Button>
                                    </Popconfirm>
                                    <Popconfirm title="Reject this request?" onConfirm={() => handleApproval('Rejected')} okText="Yes" cancelText="No">
                                        <Button danger block icon={<CloseOutlined />} loading={processing}>
                                            Reject Request
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <div className="fluid-card" style={{ marginTop: 16 }}>
                        <div className="fluid-card-header">
                            <h3 className="fluid-card-title">Audit Information</h3>
                        </div>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Reviewed By">{prDetails.approver_name || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Reviewed Date">{prDetails.approved_dt ? dayjs(prDetails.approved_dt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                            <Descriptions.Item label="Review Remarks" span={2}>{prDetails.approved_comment || '-'}</Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default PRApprovalDetail;
